import {
  aboutYouInitialState,
  accountDetailsInitialState,
  financialDetailsInitialState,
  memberDetailsInitialState,
} from '../store/initialState';
import { ApplicantState, CommonState } from '../store/types';
import { IAccountDetails, IAccountsToOpenDetails } from './accountDetailsType';
import { IPersonalDetails } from './personalDetailsType';

export const mapToPersonalDetailsDto = (
  personalDetails: ApplicantState,
  currentApplicantIndex: number,
  commonDetails: CommonState,
  isComplete: boolean,
  saveForLater: boolean
): IPersonalDetails => {
  const personalDetailsDto: IPersonalDetails = {
    isComplete: isComplete,
    currentStepId: saveForLater
      ? commonDetails.currentStep
      : commonDetails.nextStep,
    currentStepIndex: saveForLater
      ? commonDetails.currentStepIndex
      : commonDetails.nextStepIndex,
    stepToValidate: commonDetails.currentStep,
    currentApplicantIndex: currentApplicantIndex,
    isMinorApplication: commonDetails.isMinorApplication,
    utmCampaignCode: commonDetails.utmCampaignCode,
    referenceNumber: commonDetails.referenceNumber,
    applicantId: personalDetails.applicantId,
    memberDetails: {
      ...personalDetails.memberDetails,
      // we treat credit card addtional cardholder(s) as normal applicant(s) in the front end
      // so we need to notify API layer if the memebr is an addtional cardholder
      isAdditionalCardholder:
        commonDetails.accountType === 'CC' &&
        !personalDetails.memberDetails.isPrimary,
    },
    aboutYou: {
      ...personalDetails.aboutYou,
      accountType: commonDetails.accountType,
      birthday: personalDetails.aboutYou.birthday || null,
      previousAddressRequired:
        (personalDetails.aboutYou.currentAddressYearLength || 0) < 3,
      postalAddressRequired:
        personalDetails.aboutYou.mailingDestination === 'Elsewhere',
      residentialAddressSameAsPrimary:
        personalDetails.aboutYou.addressSameAsPrimary,
      postalAddressSameAsPrimary: personalDetails.aboutYou.addressSameAsPrimary,
      annualReports: personalDetails.memberDetails.isExistingMember
        ? null
        : personalDetails.aboutYou.annualReports,
      noticeOfMeetings: personalDetails.memberDetails.isExistingMember
        ? null
        : personalDetails.aboutYou.noticeOfMeetings,
    },
    verifyIdentity: { ...personalDetails.verifyIdentity },
    taxDetails: { ...personalDetails.taxDetails },
  };

  return personalDetailsDto;
};

export const mapToAccountDetailsDto = (
  personalDetails: ApplicantState,
  currentApplicantIndex: number,
  commonDetails: CommonState,
  saveForLater: boolean
): IAccountDetails => {
  // Regardless of the type of the application you use, commonDetails.accountType is always
  // the primary account type you apply for and additionalAccountToOpen is always the additional
  // account type you want to add. Even for online saving where we need to  add additional everyday
  // account as the first account to open if an everyday account is required
  // commonDetails.accountType is "OS" and additionalAccountToOpen is "EA".

  // For online saving, we must add additional everyday account as the first account to open
  // if an everyday account is required in which case additionalAccountAdded is true
  let productCode =
    commonDetails.accountType === 'OS'
      ? personalDetails.accountDetails.additionalAccountAdded
        ? personalDetails.accountDetails.additionalAccountToOpen
        : commonDetails.accountType
      : commonDetails.accountType;

  // create an array for accounts to open and add the first account to it
  // please note, productCode for the first account is EA if you apply for
  // an online saving account and an everyday account is required
  let accountsToOpen: IAccountsToOpenDetails[] = [
    {
      Sequence: 1,
      ProductCode: productCode,
      offsetAccountRequired:
        productCode === 'EA'
          ? personalDetails.accountDetails.offsetAccountRequired
          : false,
      LinkedAccountNumber:
        productCode === 'EA'
          ? personalDetails.accountDetails.offsetAccount
          : '',

      // commonDetails.accountType is the primary account type you apply for
      // that matches the account type in the url
      // When you apply for an online saving account, productCode for the first
      // account to open might be "EA". In this case, productCode is different
      // from commonDetails.accountType that is "OS". So we need to use the
      // value from cardTypeAdditionalAccount and cardColorAdditional
      cardType:
        commonDetails.accountType === 'MA'
          ? null
          : productCode === commonDetails.accountType
          ? personalDetails.accountDetails.cardType
          : personalDetails.accountDetails.cardTypeAdditionalAccount,
      cardColor:
        commonDetails.accountType === 'MA'
          ? null
          : productCode === commonDetails.accountType
          ? personalDetails.accountDetails.cardColorMain
          : personalDetails.accountDetails.cardColorAdditional,
      addCard:
        commonDetails.accountType === 'MA'
          ? null
          : productCode === commonDetails.accountType
          ? personalDetails.accountDetails.addCardMain
          : personalDetails.accountDetails.addCardAdditional,
    },
  ];

  // Now we need to add the second account to open. For online saving application,
  // we added "EA" as thefirst account to open so we now have to add primary account
  // type that is "OS" saved in commonDetails.accountType to the list
  // For none online saving account, just use the value in additionalAccountToOpen
  // because we don;t need to change the sequence of the accounts to open and
  // second account is always stored as additional account
  productCode =
    commonDetails.accountType === 'OS'
      ? commonDetails.accountType
      : personalDetails.accountDetails.additionalAccountToOpen;

  if (personalDetails.accountDetails.additionalAccountAdded === true) {
    accountsToOpen.push({
      Sequence: 2,
      ProductCode: productCode,
      offsetAccountRequired:
        productCode === 'EA'
          ? personalDetails.accountDetails.offsetAccountRequired
          : false,

      LinkedAccountNumber:
        productCode === 'EA'
          ? personalDetails.accountDetails.offsetAccount
          : '',
      // For none online saving account, productCode is same as additionalAccountToOpen
      // if we choose to add an additional account in the account details step.
      // If not, we just use the default value stored in cardTypeAdditionalAccount
      // and cardColorAdditional which should be null

      // For online saving account, productCode is "OS" that is different from additionalAccountToOpen
      // which is "EA".
      cardType:
        productCode === personalDetails.accountDetails.additionalAccountToOpen
          ? personalDetails.accountDetails.cardTypeAdditionalAccount
          : personalDetails.accountDetails.cardType,
      cardColor:
        productCode === personalDetails.accountDetails.additionalAccountToOpen
          ? personalDetails.accountDetails.cardColorAdditional
          : personalDetails.accountDetails.cardColorMain,
      addCard:
        productCode === personalDetails.accountDetails.additionalAccountToOpen
          ? personalDetails.accountDetails.addCardAdditional
          : personalDetails.accountDetails.addCardMain,
    });
  }

  let accountDetailsDto: IAccountDetails = {
    isComplete: true,
    accountType: commonDetails.accountType,
    currentStepId:
      commonDetails.accountType === 'MA'
        ? commonDetails.currentStep
        : saveForLater
        ? commonDetails.currentStep
        : commonDetails.nextStep,
    currentStepIndex:
      commonDetails.accountType === 'MA'
        ? commonDetails.currentStepIndex
        : saveForLater
        ? commonDetails.currentStepIndex
        : commonDetails.nextStepIndex,
    currentApplicantIndex: currentApplicantIndex,
    isMinorApplication: commonDetails.isMinorApplication,
    utmCampaignCode: commonDetails.utmCampaignCode,
    referenceNumber: commonDetails.referenceNumber,
    applicantId: personalDetails.applicantId,
    haveCampaignCode:
      commonDetails.accountType === 'CC'
        ? personalDetails.financialDetails.haveCampaignCode
        : personalDetails.accountDetails.haveCampaignCode,
    campaignCode:
      (commonDetails.accountType === 'CC'
        ? personalDetails.financialDetails.campaignCode
        : personalDetails.accountDetails.campaignCode) ||
        commonDetails.utmCampaignCode,
    accountsToOpen: accountsToOpen,
    everydayAccountDetails: null,
    termDepositAccountDetails: null,
    creditCardAccountDetails: null,
  };

  if (commonDetails.accountType === 'EA') {
    accountDetailsDto.everydayAccountDetails = {
      offsetAccount: personalDetails.accountDetails.offsetAccount,
      offsetAccountRequired:
        personalDetails.accountDetails.offsetAccountRequired,
    };
  } else if (commonDetails.accountType === 'FIA') {
    accountDetailsDto.termDepositAccountDetails = {
      depositAmount: personalDetails.accountDetails.depositAmount,
      interestPaymentOption:
        personalDetails.accountDetails.interestPaymentOption,
      investmentTermType: personalDetails.accountDetails.investmentTermType,
      investmentTerm: personalDetails.accountDetails.investmentTerm,
      depositMethod: personalDetails.accountDetails.depositMethod,
      interestPaidTo: personalDetails.accountDetails.interestPaidTo,
      interestRate: personalDetails.accountDetails.interestRate,
      paidToNpbsAccountName:
        personalDetails.accountDetails.paidToNpbsAccountName,
      paidToNpbsAccountNumber:
        personalDetails.accountDetails.paidToNpbsAccountNumber,
      paidToOtherAccountName:
        personalDetails.accountDetails.paidToOtherAccountName,
      paidToOtherBsb: personalDetails.accountDetails.paidToOtherBsb,
      paidToOtherAccountNumber:
        personalDetails.accountDetails.paidToOtherAccountNumber,
      methodOfOperation: personalDetails.accountDetails.methodOfOperation,
    };
  } else if (commonDetails.accountType === 'CC') {
    accountDetailsDto.creditCardAccountDetails = {
      ...personalDetails.financialDetails,
      numberOfDependants: personalDetails.financialDetails.haveDependants
        ? personalDetails.financialDetails.numberOfDependants
        : 0,
    };
  }

  return accountDetailsDto;
};

export const mapRetrievalToRedux = (retrievalData: any[]): ApplicantState[] => {
  let applicantsData = [] as ApplicantState[];

  retrievalData.forEach((applicant) => {
    const applicantData: ApplicantState = {
      applicantId: applicant.applicantId,
      memberDetails: {
        ...memberDetailsInitialState,
        ...applicant.memberDetails,
        relationshipType:
          applicant.memberDetails.relationshipType ||
          memberDetailsInitialState.relationshipType,
      },
      aboutYou: {
        ...aboutYouInitialState,
        ...applicant.aboutYou,
      },
      verifyIdentity: applicant.verifyIdentity,
      taxDetails: applicant.taxDetails,
      accountDetails: {
        ...accountDetailsInitialState,
        ...applicant.accountDetails,
        additionalAccountToOpen:
          applicant.accountDetails?.additionalAccountToOpen ||
          accountDetailsInitialState.additionalAccountToOpen,
        cardColorMain:
          applicant.accountDetails?.cardColorMain ||
          accountDetailsInitialState.cardColorMain,
        cardColorAdditional:
          applicant.accountDetails?.cardColorAdditional ||
          accountDetailsInitialState.cardColorAdditional,
        cardType:
          applicant.accountDetails?.cardType ||
          accountDetailsInitialState.cardType,
        cardTypeAdditionalAccount:
          applicant.accountDetails?.cardTypeAdditionalAccount ||
          accountDetailsInitialState.cardTypeAdditionalAccount,
        addCardMain:
          applicant.accountDetails?.addCardMain ||
          accountDetailsInitialState.addCardMain,
        addCardAdditional:
          applicant.accountDetails?.addCardAdditional ||
          accountDetailsInitialState.addCardAdditional,
        methodOfOperation:
          applicant.accountDetails?.methodOfOperation ||
          accountDetailsInitialState.methodOfOperation,
      },
      financialDetails: {
        ...financialDetailsInitialState,
        ...applicant.financialDetails,
      },
    };

    applicantsData.push(applicantData);
  });

  return applicantsData;
};
